import { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';

const MAX_VIDEO_TIMEOUT = 65;
const MAX_RETRIES = 2;
const DYNAMIC_STREAM_TEMPORARY_LIMIT_SECONDS = 10 * 60;
const MIN_STABLE_PLAYBACK_OFFSET_SECONDS = 0;

const getCacheBustedUrl = (url) => {
  const [baseUrl, queryString] = url.split('?');
  const params = new URLSearchParams(queryString || '');
  params.set('nocache', Date.now());
  return `${baseUrl}?${params.toString()}`;
};

const isMP4 = (url) => url && url.toLowerCase().includes('.mp4');
const isHLS = (url) => url && url.toLowerCase().includes('.m3u8');

// Safari supports HLS natively via <video> element
const nativeHLSSupported = (videoEl) =>
  videoEl && typeof videoEl.canPlayType === 'function' &&
  videoEl.canPlayType('application/vnd.apple.mpegurl') !== '';

const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,
  startPosition: 0,
  backBufferLength: 8,
  maxBufferLength: 12,
  maxMaxBufferLength: 20,
  maxBufferSize: 20 * 1000 * 1000,
  maxConcurrentLoadingSessions: 3,
  fragLoadingTimeOut: 10000,
  manifestLoadingTimeOut: 10000,
  levelLoadingTimeOut: 10000,
  fragLoadingMaxRetry: 2,
  manifestLoadingMaxRetry: 2,
};

const INITIAL_STATE = {
  isPlaying: false,
  progress: 0,
  speed: 1,
  isMuted: false,
  isVideoLoad: false,
  isLive: false,
  isDisabled: false,
  playStatus: 'Not Ready',
  videoType: null,
  isTimeOut: false,
  isLoading: false,
  liveTimeOut: 0
};

function checkVideoExists(url) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => { video.src = ''; video.load(); };
    video.onloadedmetadata = () => { cleanup(); resolve(true); };
    video.onerror = () => { cleanup(); resolve(false); };
    video.src = url;
  });
}

const useHLSVideoPlayer = (appStatus, videoElement, liveURL, videoURL, maxTimeOut) => {
  const hlsRef = useRef(null);
  const intervalRef = useRef(null);
  const retryAttempts = useRef(0);
  const lastUpdateRef = useRef(0);
  const isVideoLive = useRef(0);
  const videoRiseOn = useRef(0);
  const source = useRef(null);
  const lastSegmentTime = useRef(0);
  const wantsPlayRef = useRef(true);

  const [playerState, setPlayerState] = useState(INITIAL_STATE);

  const seekToBufferedRange = (video) => {
    if (!video || video.buffered.length === 0) return;
    const firstBuffered = video.buffered.start(0);
    const lastBuffered = video.buffered.end(video.buffered.length - 1);
    const bufferedSpan = Math.max(0, lastBuffered - firstBuffered);
    const desiredStart = bufferedSpan > (MIN_STABLE_PLAYBACK_OFFSET_SECONDS + 0.1)
      ? firstBuffered + MIN_STABLE_PLAYBACK_OFFSET_SECONDS
      : firstBuffered + 0.05;
    const target = Math.min(
      Math.max(desiredStart, firstBuffered + 0.05),
      Math.max(firstBuffered + 0.05, lastBuffered - 0.05)
    );
    if (video.currentTime < target || video.currentTime > lastBuffered) {
      video.currentTime = target;
    }
  };

  const safePlay = (video) => {
    if (!video) return;
    seekToBufferedRange(video);
    video.play().catch(() => {});
  };

  const cleanPlayer = (isUpdateState = true) => {
    if (hlsRef.current || (videoElement.current && source.current)) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      } else if (videoElement.current) {
        source.current = '';
        videoElement.current.src = '';
        videoElement.current.load();
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (isUpdateState) {
        setPlayerState(INITIAL_STATE);
        retryAttempts.current = 0;
        isVideoLive.current = null;
        videoRiseOn.current = null;
        lastSegmentTime.current = 0;
      }
    }
  };

  const loadHlsVideo = (videoSource, isUpdateState = true) => {
    if (!videoElement.current) return;

    if (Hls.isSupported()) {
      if (hlsRef.current) cleanPlayer(isUpdateState);

      const hls = new Hls(HLS_CONFIG);
      hlsRef.current = hls;
      hls.attachMedia(videoElement.current);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(videoSource);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (videoElement.current && wantsPlayRef.current) {
          safePlay(videoElement.current);
        }
      });

      hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
        isVideoLive.current = data.details.live;
      });

      hls.on(Hls.Events.FRAG_LOADED, () => {
        lastSegmentTime.current = Date.now();
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (['bufferStalledError', 'fragParsingError', 'fragLoadError'].includes(data.details)) return;
        if (data.fatal) onVideoError(data);
      });

      let seekAdjusted = false;
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        if (seekAdjusted) return;
        const video = videoElement.current;
        if (!video) return;
        if (video.buffered.length === 0) return;
        seekAdjusted = true;
        const bufStart = video.buffered.start(0);
        if (bufStart > 0.5 && video.currentTime <= bufStart) {
          video.currentTime = bufStart;
          if (!isVideoLive.current) {
            video.addEventListener('canplay', function onCanPlay() {
              video.removeEventListener('canplay', onCanPlay);
              video.play().catch(() => {});
            }, { once: true });
          }
        }

        if (video.paused && wantsPlayRef.current && video.buffered.length > 0) {
          const bufferedEnd = video.buffered.end(video.buffered.length - 1);
          if (bufferedEnd >= 1) {
            safePlay(video);
          }
        }
      });

    } else if (nativeHLSSupported(videoElement.current)) {
      cleanPlayer(isUpdateState);
      const video = videoElement.current;
      video.src = videoSource;
      video.load();
      source.current = videoSource;
      video.onerror = (error) => onVideoError(error);
    }

    source.current = videoSource;
  };

  const getRemainLiveTime = () => {
    const remainTime = parseInt(Math.max(0, MAX_VIDEO_TIMEOUT - maxTimeOut));
    if (remainTime && videoRiseOn.current)
      return parseInt(Math.max(0, remainTime - ((Date.now() - videoRiseOn.current) / 1000)));
    return remainTime;
  };

  const mp4VideoLoad = (videoSource, isUpdateState = true) => {
    if (!videoElement.current) return;
    cleanPlayer(isUpdateState);
    const video = videoElement.current;
    video.src = videoSource;
    video.load();
    source.current = videoSource;
    video.onerror = (error) => onVideoError(error);
  };

  const onVideoLoaded = () => {
    const videoType = isMP4(source.current) ? 'mp4' : 'hls';
    const videoTimer = videoType === 'hls' ? getRemainLiveTime() : 0;

    if (videoType === 'mp4' && isVideoLive.current === true) isVideoLive.current = false;

    const isLive = videoTimer > 0 && isVideoLive.current;

    if (!isLive && videoElement.current) videoElement.current.playbackRate = playerState.speed;

    setPlayerState((prev) => ({
      ...prev,
      isVideoLoad: true,
      isLive,
      isDisabled: isLive,
      isPlaying: wantsPlayRef.current ? true : prev.isPlaying,
      isTimeOut: !isLive,
      playStatus: isLive ? 'Live' : 'Ready',
      videoType,
      liveTimeOut: videoTimer,
    }));

    if (videoElement.current) {
      wantsPlayRef.current = true;
      safePlay(videoElement.current);
    }
  };

  const changeHLS2Mp4 = () => {
    cleanPlayer(false);
    source.current = videoURL;
    videoElement.current.src = videoURL;
    videoElement.current.load();
    setPlayerState((prev) => ({ ...prev, videoType: 'mp4' }));
  };

  const reloadVideo = () => {
    const video = videoElement.current;
    if (!video || !source.current) return;
    const src = source.current;
    if (isHLS(src)) {
      if (videoRiseOn.current && ((Date.now() - videoRiseOn.current) / 1000) + maxTimeOut >= parseInt(process.env.REACT_APP_HLS_MAX_TIME) * 60) {
        changeHLS2Mp4();
      } else {
        loadHlsVideo(getCacheBustedUrl(src), false);
      }
    } else if (isMP4(src)) {
      mp4VideoLoad(source.current, false);
    }
  };

  const onVideoError = (_e) => {
    const video = videoElement.current;
    if (
      video && source.current &&
      ((source.current.toLowerCase().includes('.mp4') && !isVideoLive.current) ||
        source.current.toLowerCase().includes('.m3u8'))
    ) {
      const erroredURL = source.current;
      if (retryAttempts.current < MAX_RETRIES) {
        retryAttempts.current += 1;
        setTimeout(() => reloadVideo(), 2000);
      } else {
        setPlayerState((prev) => ({
          ...prev,
          isVideoLoad: false,
          playStatus: 'Error',
          isLive: false,
          isDisabled: true,
          isTimeOut: true,
          liveTimeOut: 0,
          videoType: erroredURL.toLowerCase().includes('.mp4') ? 'mp4' : 'hls',
        }));
      }
    }
  };

  const handlePlay = () => {
    const video = videoElement.current;
    if (!video) return;
    wantsPlayRef.current = true;
    setPlayerState((prev) => {
      if (!prev.isLive) video.playbackRate = prev.speed;
      return { ...prev, isPlaying: true, playStatus: 'Playing...' };
    });
  };

  const handlePause = () => {
    setPlayerState((prev) => ({ ...prev, isPlaying: false, playStatus: 'Paused' }));
  };

  const handleVideoSpeed = (e) => {
    const speed = parseFloat(e.target.value);
    const video = videoElement.current;
    if (!video || playerState.isDisabled) return;
    video.playbackRate = speed;
    setPlayerState((prev) => ({ ...prev, speed }));
  };

  const handleOnTimeUpdate = () => {
    const now = Date.now();
    if (now - lastUpdateRef.current < 500) return;
    lastUpdateRef.current = now;

    const video = videoElement.current;
    if (!video) return;

    const duration = video.duration;
    const current = video.currentTime;
    const progress = (current / duration) * 100;
    const playStatus = current >= duration ? 'Completed' : 'Playing...';

    setPlayerState((prev) => ({ ...prev, progress, playStatus }));
  };

  const togglePlay = () => {
    const video = videoElement.current;
    if (!video || playerState.isDisabled) return;
    if (playerState.isPlaying) {
      wantsPlayRef.current = false;
      video.pause();
      setPlayerState((prev) => ({ ...prev, isPlaying: false, playStatus: 'Paused' }));
    } else {
      wantsPlayRef.current = true;
      setPlayerState((prev) => ({ ...prev, isPlaying: true, playStatus: 'Playing...' }));
      safePlay(video);
    }
  };

  useEffect(() => {
    const video = videoElement.current;
    if (!video || playerState.isDisabled) return;

    if (!playerState.isPlaying) {
      wantsPlayRef.current = false;
      if (!video.paused) video.pause();
      return;
    }
    wantsPlayRef.current = true;

    const attemptPlay = () => { safePlay(video); };
    attemptPlay();
    video.addEventListener('loadedmetadata', attemptPlay);
    video.addEventListener('canplay', attemptPlay);
    video.addEventListener('canplaythrough', attemptPlay);

    return () => {
      video.removeEventListener('loadedmetadata', attemptPlay);
      video.removeEventListener('canplay', attemptPlay);
      video.removeEventListener('canplaythrough', attemptPlay);
    };
  }, [playerState.isPlaying, playerState.isDisabled, videoURL, liveURL]);

  useEffect(() => {
    if (playerState.liveTimeOut > 0 && intervalRef.current === null) {
      intervalRef.current = setInterval(() => {
        setPlayerState((prev) => {
          const remainTime = Math.max(prev.liveTimeOut - 1, 0);
          const newTime = isVideoLive.current === false ? 0 : remainTime;
          let isLive = true;

          if (newTime <= 0) {
            isLive = false;
            if (videoElement.current) videoElement.current.playbackRate = playerState.speed;
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          if (
            prev.videoType === 'hls' && isVideoLive.current === true &&
            lastSegmentTime.current && (Date.now() - lastSegmentTime.current) / 1000 > 5
          ) {
            if (retryAttempts.current < MAX_RETRIES) {
              retryAttempts.current += 1;
              reloadVideo();
            }
          }

          if (remainTime <= 0 && isVideoLive.current === true) {
            setTimeout(() => {
              prev.videoType === 'hls' ? reloadVideo() : mp4VideoLoad(source.current, false);
            }, 1000);
          } else if (prev.videoType === 'mp4') {
            checkVideoExists(source.current).then((exists) => {
              if (exists) mp4VideoLoad(source.current, false);
            });
          }

          return {
            ...prev,
            isLive,
            playStatus: isLive ? 'Live' : 'Ready',
            isTimeOut: newTime === 0,
            liveTimeOut: newTime,
          };
        });
      }, 1000);
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [playerState.liveTimeOut]);

  useEffect(() => {
    if (!videoElement.current) return;
    if (!appStatus || !videoURL) return cleanPlayer();

    videoRiseOn.current = Date.now();
    wantsPlayRef.current = true;
    const liveTimeOut = getRemainLiveTime();

    const isDynamicStream = videoURL.includes('dynamic.m3u8');
    const isOlderThanTemporaryWindow = Number(maxTimeOut) > DYNAMIC_STREAM_TEMPORARY_LIMIT_SECONDS;
    if (isDynamicStream && liveURL && !isOlderThanTemporaryWindow) {
      loadHlsVideo(liveURL);
      return;
    }
    if (isDynamicStream && isOlderThanTemporaryWindow && isHLS(videoURL)) {
      loadHlsVideo(videoURL);
      return;
    }

    if (!liveURL) {
      if (isHLS(videoURL)) {
        loadHlsVideo(videoURL);
      } else {
        mp4VideoLoad(videoURL);
      }
      return;
    }

    checkVideoExists(videoURL).then((exists) => {
      if (exists) {
        mp4VideoLoad(videoURL);
      } else if (isHLS(liveURL)) {
        loadHlsVideo(liveURL);
      } else {
        const raiseTime = videoRiseOn.current;
        cleanPlayer();
        videoRiseOn.current = raiseTime;
        isVideoLive.current = true;
        source.current = videoURL;

        const isPlayLive = getRemainLiveTime() > 0;
        setPlayerState((prev) => ({
          ...prev,
          isVideoLoad: isPlayLive,
          isLive: isPlayLive,
          isDisabled: isPlayLive,
          isTimeOut: !isPlayLive,
          playStatus: isPlayLive ? 'Live' : 'Error',
          videoType: 'mp4',
          liveTimeOut,
        }));
      }
    });

    return () => {
      if (hlsRef.current) cleanPlayer();
    };
  }, [appStatus, videoURL]);

  return {
    playerState,
    togglePlay,
    handleVideoSpeed,
    handlePause,
    handlePlay,
    onVideoLoaded,
    onVideoError,
    handleOnTimeUpdate,
    reloadVideo,
  };
};

export default useHLSVideoPlayer;
